/**
 * The view editor's metric columns, as AgencyAnalytics' Column Settings: a
 * name, Value (one metric per data source row) or Computed Value (a formula
 * over value columns), the data type, how the totals row adds it up (sum,
 * average, min, max or none), and whether an increase is good.
 */

import type { ReactNode } from 'react';

import type { CatalogueSource } from '../reports/reports-api.ts';
import type { Aggregation, DataType, ViewColumn, ViewDefinition } from './custom-api.ts';
import { FormulaEditor } from './FormulaEditor.tsx';
import { newId } from './ViewSources.tsx';

const DATA_TYPES: [DataType, string][] = [
  ['INTEGER', 'Integer'],
  ['FLOAT', 'Float'],
  ['CURRENCY', 'Currency'],
  ['PERCENT', 'Percent'],
];
const AGGREGATIONS: [Aggregation, string][] = [
  ['SUM', 'Sum'],
  ['AVG', 'Average'],
  ['MIN', 'Min'],
  ['MAX', 'Max'],
  ['NONE', 'None'],
];
const MAX_COLUMNS = 25;

function blankColumn(kind: ViewColumn['kind']): ViewColumn {
  return {
    id: newId('c'),
    name: kind === 'VALUE' ? 'New column' : 'New computed column',
    kind,
    data_type: 'FLOAT',
    aggregation: 'SUM',
    higher_is_better: true,
    metrics: {},
    formula: kind === 'COMPUTED' ? [] : null,
  };
}

function Options<T extends string>({ options }: { options: [T, string][] }): ReactNode {
  return options.map(([value, label]) => (
    <option key={value} value={value}>
      {label}
    </option>
  ));
}

export function ViewColumns(props: {
  definition: ViewDefinition;
  sources: CatalogueSource[];
  onChange: (definition: ViewDefinition) => void;
}): ReactNode {
  const { definition, sources, onChange } = props;
  const valueColumns = definition.columns.filter((column) => column.kind === 'VALUE');
  const setColumn = (id: string, patch: Partial<ViewColumn>) =>
    onChange({
      ...definition,
      columns: definition.columns.map((column) =>
        column.id === id ? { ...column, ...patch } : column,
      ),
    });
  const add = (kind: ViewColumn['kind']) =>
    onChange({ ...definition, columns: [...definition.columns, blankColumn(kind)] });
  return (
    <section className="cx-section" aria-labelledby="vc-title">
      <h3 id="vc-title">Metric columns</h3>
      {definition.columns.map((column) => (
        <fieldset className="cx-column" key={column.id}>
          <legend className="sr-only">{column.name}</legend>
          <div className="cx-row">
            <input
              aria-label="Column Name"
              value={column.name}
              maxLength={120}
              onChange={(event) => setColumn(column.id, { name: event.target.value })}
            />
            <select
              aria-label="Column Type"
              value={column.kind}
              onChange={(event) =>
                setColumn(column.id, {
                  kind: event.target.value as ViewColumn['kind'],
                  formula: event.target.value === 'COMPUTED' ? [] : null,
                })
              }
            >
              <option value="VALUE">Value</option>
              <option value="COMPUTED">Computed Value</option>
            </select>
            <select
              aria-label="Data Type"
              value={column.data_type}
              onChange={(event) =>
                setColumn(column.id, { data_type: event.target.value as DataType })
              }
            >
              <Options options={DATA_TYPES} />
            </select>
            <select
              aria-label="Aggregation Type"
              value={column.aggregation}
              onChange={(event) =>
                setColumn(column.id, { aggregation: event.target.value as Aggregation })
              }
            >
              <Options options={AGGREGATIONS} />
            </select>
            <select
              aria-label="Change Format"
              value={column.higher_is_better ? 'up' : 'down'}
              onChange={(event) =>
                setColumn(column.id, { higher_is_better: event.target.value === 'up' })
              }
            >
              <option value="up">Increase is positive</option>
              <option value="down">Decrease is positive</option>
            </select>
            <button
              type="button"
              className="ex-link"
              onClick={() =>
                onChange({
                  ...definition,
                  columns: definition.columns.filter((item) => item.id !== column.id),
                })
              }
            >
              Remove
            </button>
          </div>
          {column.kind === 'VALUE' ? (
            <div className="cx-row cx-metrics">
              {definition.sources.map((row) => {
                const source = sources.find((item) => item.key === row.source);
                return (
                  <label key={row.id} className="kp-inline">
                    {source?.label ?? row.source}
                    <select
                      value={column.metrics[row.id] ?? ''}
                      onChange={(event) => {
                        const metrics = { ...column.metrics };
                        if (event.target.value) metrics[row.id] = event.target.value;
                        else delete metrics[row.id];
                        setColumn(column.id, { metrics });
                      }}
                    >
                      <option value="">—</option>
                      {source?.metrics.map((metric) => (
                        <option key={metric.key} value={metric.key}>
                          {metric.label}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              })}
            </div>
          ) : (
            <FormulaEditor
              tokens={column.formula ?? []}
              choices={{
                kind: 'columns',
                columns: valueColumns.map((item) => ({ id: item.id, name: item.name })),
              }}
              onChange={(formula) => setColumn(column.id, { formula })}
            />
          )}
        </fieldset>
      ))}
      <div className="cx-row">
        <button
          type="button"
          className="btn secondary"
          disabled={definition.columns.length >= MAX_COLUMNS}
          onClick={() => add('VALUE')}
        >
          + Add column
        </button>
        <button
          type="button"
          className="btn secondary"
          disabled={definition.columns.length >= MAX_COLUMNS || valueColumns.length === 0}
          onClick={() => add('COMPUTED')}
        >
          + Add computed column
        </button>
      </div>
    </section>
  );
}
